import apiClient from './client';

/**
 * MED-784 Session Content API client.
 *
 * Backend: medley-api/routes/sessionContent.ts mounted under
 * /api/cohorts/:cohortId/sessions/:sessionId/content[/:slot[/retry]].
 *
 * Drive-URL flow lives in MED-871 follow-up; this client is file-upload only.
 */

export type SessionContentSlot = 'session_guide' | 'slide_deck';

export type SessionContentProcessingState = 'pending' | 'processing' | 'ready' | 'failed';

export type SessionContentCalloutType =
  | 'presence'
  | 'coach_callout'
  | 'coach_tip'
  | 'key_points'
  | 'guiding_question'
  | 'other';

export interface SessionContentCallout {
  type: SessionContentCalloutType;
  text: string;
}

export interface SessionContentBlock {
  title: string;
  page: number;
  duration_minutes: number | null;
  text: string;
  callouts: SessionContentCallout[];
  slide_pages?: number[];
}

export interface SessionContentSection {
  title: string;
  page: number;
  duration_minutes: number | null;
  children: SessionContentBlock[];
}

export type SessionContentParsedOutline = SessionContentSection[];

export interface SessionContentFile {
  id: string;
  session_id: string;
  slot: SessionContentSlot;
  original_filename: string;
  mime_type: string;
  source_s3_key: string;
  converted_pdf_s3_key: string | null;
  parsed_outline: SessionContentParsedOutline | null;
  processing_state: SessionContentProcessingState;
  error_message: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

/**
 * MED-964 / E1.3a — header context for the CM Session Content editor page
 * (session title, cohort name, org name). Supplied by the same GET content
 * call so the page renders the header without a second round-trip.
 */
export interface SessionContentHeader {
  title: string;
  session_number: number | null;
  cohort_name: string | null;
  organization_name: string | null;
}

export interface SessionContentListResponse {
  files: SessionContentFile[];
  /** AC8: true when the session's modality has AI features enabled. */
  ai_features_enabled: boolean;
  /** MED-964: page-header context (session title, cohort name, org name). */
  session: SessionContentHeader;
}

export const sessionContentApi = {
  /** GET /api/cohorts/:cohortId/sessions/:sessionId/content */
  list: async (cohortId: string, sessionId: string): Promise<SessionContentListResponse> => {
    return apiClient.get<SessionContentListResponse>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/content`,
    );
  },

  /**
   * MED-1086 (E1.3m) — by-session-number content list for the Build Content
   * entry point reachable before groups exist. Same response shape as `list`;
   * `files` is empty until a deck is uploaded (deck upload stays on the
   * sessionId path).
   */
  listByNumber: async (
    cohortId: string,
    sessionNumber: number,
  ): Promise<SessionContentListResponse> => {
    return apiClient.get<SessionContentListResponse>(
      `/api/cohorts/${cohortId}/sessions/by-number/${sessionNumber}/content`,
    );
  },

  /** POST multipart upload for a slot. AC2 + AC6 replace flow. */
  upload: async (
    cohortId: string,
    sessionId: string,
    slot: SessionContentSlot,
    file: File,
  ): Promise<{ file: SessionContentFile }> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.postFormData<{ file: SessionContentFile }>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/content/${slot}`,
      formData,
    );
  },

  /** POST retry for a failed conversion (AC11). */
  retry: async (
    cohortId: string,
    sessionId: string,
    slot: SessionContentSlot,
  ): Promise<{ file: SessionContentFile }> => {
    return apiClient.post<{ file: SessionContentFile }>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/content/${slot}/retry`,
    );
  },

  /** DELETE the active row for a slot. */
  removeSlot: async (
    cohortId: string,
    sessionId: string,
    slot: SessionContentSlot,
  ): Promise<void> => {
    return apiClient.delete<void>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/content/${slot}`,
    );
  },

  /**
   * Fetch the converted PDF as an ArrayBuffer for in-browser PDF.js rendering
   * (AC4 thumbnails + preview modal). Backend serves it inline (AC10), so
   * the response never triggers a browser download.
   */
  fetchPdf: async (
    cohortId: string,
    sessionId: string,
    slot: SessionContentSlot,
  ): Promise<ArrayBuffer> => {
    return apiClient.getArrayBuffer(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/content/${slot}/pdf`,
    );
  },
};
